/**
 * Save migrations — version-aware upgrade framework.
 *
 * # The contract
 *
 * Saves are versioned by `gameState.version`. When SAVE_VERSION bumps from
 * N to N+1 (because GameState gained a required field, lost a field, or
 * changed a field's semantics), a single-step migration function v_n -> v_(n+1)
 * is registered here. The framework chains registered migrations in order
 * to walk any old save up to current SAVE_VERSION.
 *
 * # Why single-step migrations
 *
 * The alternative — direct migrations like `v1 -> v3` — looks tempting because
 * it skips intermediate transformations. It's a trap. Players whose saves
 * sat at v1 for months and never went through v2 lose v2's transitional
 * logic (default values for new fields, normalization fixes, data cleanup
 * that can't be reconstructed from v1 alone). Single-step migrations
 * guarantee every save passes through every transformation in order.
 *
 * # Pure-functional, no I/O
 *
 * `migrate()` takes a parsed object (not a JSON string) and returns a parsed
 * object. The Phase B adapter handles JSON.parse before this and validates
 * the result against the current schema after. This keeps migrations free
 * of side effects and trivially property-testable.
 *
 * # Production registration via registerAll
 *
 * Migrations themselves live in sibling files (e.g., `v1_to_v2.ts`). The
 * `registerAll.ts` file imports each one and calls `registerMigration()`
 * for each. Production code calls `registerAllMigrations()` once at app
 * boot — typically from Phase B's adapter at IndexedDB initialization.
 *
 * Tests use `clearMigrationRegistry()` between cases for isolation; each
 * test that needs migrations registers exactly what it needs.
 *
 * # Migration error handling
 *
 * Migration functions are allowed to throw. `migrate()` catches and re-throws
 * as `MigrationError` with `fromVersion`, `toVersion`, and `cause`. Phase B
 * pattern-matches this to surface `SaveLoadFailureReason: 'migration_failed'`
 * to the UI, where the user can choose to start fresh or restore a backup.
 *
 * # Determinism
 *
 * Migration functions MUST be pure: same input -> same output. They MUST NOT
 * read clocks, generate random IDs, or call any external service. If a
 * migration needs randomness (e.g., generating a default UUID for an old save),
 * derive it deterministically from existing fields (e.g., hash the install ID).
 *
 * @see {@link ../schema.ts} — current SaveEnvelope shape
 * @see {@link ../serialize.ts} — calls migrate() before validating against schema
 * @see {@link ./registerAll.ts} — registers each migration at boot
 * @see {@link ./v1_to_v2.ts} — first real migration (combat genre pivot)
 */

import { SAVE_VERSION } from '../../state';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Signature of a migration function.
 *
 * Takes a parsed save payload at version `from` (validated by the caller —
 * the migration trusts the input shape matches v_from). Returns a payload
 * at version `from + 1`. Throws on irrecoverable input.
 *
 * The returned payload is `unknown` because chained migrations may transform
 * shapes the type system can't track ahead of time. The final result is
 * validated by `SaveEnvelopeSchema.parse()` after the chain completes.
 */
export type MigrationFn = (input: unknown) => unknown;

/**
 * Registry of registered migrations. Key is the FROM version; value is the
 * function that takes v_key and produces v_(key+1).
 */
const migrationRegistry: Map<number, MigrationFn> = new Map();

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thrown when migration fails. Carries enough context for Phase B to surface
 * a meaningful UI error and for telemetry to flag the bug.
 */
export class MigrationError extends Error {
  public readonly fromVersion: number;
  public readonly toVersion: number;
  public override readonly cause: unknown;

  constructor(
    fromVersion: number,
    toVersion: number,
    message: string,
    cause: unknown,
  ) {
    super(`Migration v${fromVersion} -> v${toVersion} failed: ${message}`);
    this.name = 'MigrationError';
    this.fromVersion = fromVersion;
    this.toVersion = toVersion;
    this.cause = cause;
  }
}

/**
 * Thrown when a save's version is HIGHER than current SAVE_VERSION (player
 * downgraded the game). This is recoverable only by re-upgrading — the
 * framework cannot invent transformations that go backwards.
 */
export class SaveTooNewError extends Error {
  public readonly saveVersion: number;
  public readonly currentVersion: number;

  constructor(saveVersion: number, currentVersion: number) {
    super(
      `Save version ${saveVersion} is newer than current SAVE_VERSION ${currentVersion}. Player likely downgraded the game.`,
    );
    this.name = 'SaveTooNewError';
    this.saveVersion = saveVersion;
    this.currentVersion = currentVersion;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register a migration v_fromVersion -> v_(fromVersion + 1).
 *
 * Throws if fromVersion is already registered (would silently overwrite,
 * which is always a bug). Tests can use `clearMigrationRegistry` to reset.
 */
export function registerMigration(fromVersion: number, fn: MigrationFn): void {
  if (!Number.isInteger(fromVersion) || fromVersion < 1) {
    throw new Error(
      `registerMigration: fromVersion must be a positive integer, got ${fromVersion}`,
    );
  }
  if (migrationRegistry.has(fromVersion)) {
    throw new Error(
      `registerMigration: v${fromVersion} -> v${fromVersion + 1} already registered`,
    );
  }
  migrationRegistry.set(fromVersion, fn);
}

/**
 * Clear all registered migrations. Used by tests to reset between cases.
 * NOT for runtime use — production migrations are registered once at app
 * boot via `registerAllMigrations()` and never cleared.
 */
export function clearMigrationRegistry(): void {
  migrationRegistry.clear();
}

/**
 * Inspect which versions have a registered migration. Returns sorted list of
 * fromVersion keys. Useful for debugging and for asserting in tests.
 */
export function listRegisteredMigrations(): readonly number[] {
  return [...migrationRegistry.keys()].sort((a, b) => a - b);
}

// ─────────────────────────────────────────────────────────────────────────────
// Migration chain runner
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply registered migrations to bring a save from its stored version up to
 * current SAVE_VERSION.
 *
 * # Input
 *
 * `payload` is the parsed save object — typically the deserialized JSON from
 * IndexedDB, possibly missing fields the current schema requires. Caller is
 * responsible for extracting the version: this function takes both the
 * payload AND the version separately.
 *
 * # Output
 *
 * Returns the migrated payload, ready for schema validation. NOT validated
 * by this function — the caller (typically `serialize.deserialize`) is
 * expected to run `SaveEnvelopeSchema.parse()` next.
 *
 * # Errors
 *
 * - `SaveTooNewError` if version > SAVE_VERSION (player downgraded the game)
 * - `MigrationError` if a registered migration throws
 * - `Error` (generic) if a registered migration is missing for an
 *   intermediate version (registry has v1->v2 and v3->v4 but no v2->v3)
 *
 * # Determinism
 *
 * Pure function. Same input -> same output. Property-testable.
 */
export function migrate(payload: unknown, fromVersion: number): unknown {
  if (!Number.isInteger(fromVersion) || fromVersion < 1) {
    throw new Error(
      `migrate: fromVersion must be a positive integer, got ${fromVersion}`,
    );
  }

  if (fromVersion > SAVE_VERSION) {
    throw new SaveTooNewError(fromVersion, SAVE_VERSION);
  }

  if (fromVersion === SAVE_VERSION) {
    // Already current. No transformations needed.
    return payload;
  }

  let current: unknown = payload;
  let v = fromVersion;

  while (v < SAVE_VERSION) {
    const fn = migrationRegistry.get(v);
    if (!fn) {
      throw new Error(
        `migrate: no migration registered for v${v} -> v${v + 1}. Cannot bridge gap from v${fromVersion} to v${SAVE_VERSION}.`,
      );
    }
    try {
      current = fn(current);
    } catch (cause) {
      throw new MigrationError(
        v,
        v + 1,
        cause instanceof Error ? cause.message : String(cause),
        cause,
      );
    }
    v += 1;
  }

  return current;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reference migration: noop identity
//
// Exported for tests so they can verify the registry/chain mechanics
// without needing real migrations. Production migrations live in their
// own files and are registered via `registerAll.ts`.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Identity migration. Returns input unchanged. Exported for tests.
 */
export const noopMigration: MigrationFn = (input) => input;
